import { type ReactElement } from 'react';
import { Pencil, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { cn } from '@/lib/utils';

import type { AdminIdentifierTypeViewModel } from '../api';

const EMPTY_CELL = '—';

/**
 * Locked row + header heights mirroring AdminUsersTable so the two admin
 * tables read as the same component visually.
 */
export const ADMIN_IDENTIFIER_TYPES_ROW_HEIGHT_PX = 40;
export const ADMIN_IDENTIFIER_TYPES_HEAD_HEIGHT_PX = 40;
const COMPACT_CELL_CLASS = 'h-[40px] py-0';
const COMPACT_HEAD_CLASS = 'h-[40px] py-0';
const ZEBRA_ROW_CLASS = 'even:bg-muted/55';
// Compact pill — same dimensions as the chips used in AdminUsersTable.
const COMPACT_PILL_CLASS = 'px-1.5 py-0 text-[10px] leading-[14px] sm:text-[10px]';

interface AdminIdentifierTypesTableProps {
  readonly rows: readonly AdminIdentifierTypeViewModel[];
  readonly onEdit: (row: AdminIdentifierTypeViewModel) => void;
  readonly onDelete: (row: AdminIdentifierTypeViewModel) => void;
  readonly onTogglePrimary: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleEnforceFormat: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleAllowDuplicates: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleActive: (row: AdminIdentifierTypeViewModel) => void;
  /** ID of the row currently being deleted; that row's controls render disabled. */
  readonly pendingDeleteId: string | null;
  /** ID of the row currently being saved; renders a "Saving…" hint. */
  readonly pendingUpdateId: string | null;
}

export function AdminIdentifierTypesTable({
  rows,
  onEdit,
  onDelete,
  onTogglePrimary,
  onToggleEnforceFormat,
  onToggleAllowDuplicates,
  onToggleActive,
  pendingDeleteId,
  pendingUpdateId,
}: AdminIdentifierTypesTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <DataTableTable aria-label={t('routes.admin.general.identifiers.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[12%] min-w-[5rem]">
            {t('routes.admin.general.identifiers.table.userEnteredId')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[22%] min-w-[8rem]">
            {t('routes.admin.general.identifiers.table.label')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[20%] min-w-[8rem]">
            {t('routes.admin.general.identifiers.table.format')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[12%] min-w-[6rem]">
            {t('routes.admin.general.identifiers.table.enforceFormat')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[12%] min-w-[6rem]">
            {t('routes.admin.general.identifiers.table.allowDuplicates')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[10%] min-w-[5rem]">
            {t('routes.admin.general.identifiers.table.primary')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%] min-w-[4rem]">
            {t('routes.admin.general.identifiers.table.active')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%]" align="right">
            <span className="sr-only">{t('routes.admin.general.identifiers.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <Row
            key={row.id}
            row={row}
            onEdit={onEdit}
            onDelete={onDelete}
            onTogglePrimary={onTogglePrimary}
            onToggleEnforceFormat={onToggleEnforceFormat}
            onToggleAllowDuplicates={onToggleAllowDuplicates}
            onToggleActive={onToggleActive}
            isDeleting={pendingDeleteId === row.id}
            isSaving={pendingUpdateId === row.id}
          />
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

interface RowProps {
  readonly row: AdminIdentifierTypeViewModel;
  readonly onEdit: (row: AdminIdentifierTypeViewModel) => void;
  readonly onDelete: (row: AdminIdentifierTypeViewModel) => void;
  readonly onTogglePrimary: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleEnforceFormat: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleAllowDuplicates: (row: AdminIdentifierTypeViewModel) => void;
  readonly onToggleActive: (row: AdminIdentifierTypeViewModel) => void;
  readonly isDeleting: boolean;
  readonly isSaving: boolean;
}

function Row({
  row,
  onEdit,
  onDelete,
  onTogglePrimary,
  onToggleEnforceFormat,
  onToggleAllowDuplicates,
  onToggleActive,
  isDeleting,
  isSaving,
}: RowProps): ReactElement {
  const { t } = useTranslation();
  const editLabel = t('routes.admin.general.identifiers.row.editAria', { label: row.label });
  const deleteLabel = t('routes.admin.general.identifiers.row.deleteAria', { label: row.label });
  // Inactive rows render at reduced opacity so they're visibly de-emphasised vs active ones.
  const rowClassName = cn(ZEBRA_ROW_CLASS, !row.active && 'opacity-60');
  return (
    <DataTableTr className={rowClassName}>
      <DataTableTd
        className={`${COMPACT_CELL_CLASS} pr-1 font-mono text-xs font-medium text-foreground`}
        dataOffset={false}
      >
        <span className="inline-flex items-center gap-1.5 truncate">
          {row.isPrimary && (
            <Star
              className="h-3 w-3 shrink-0 fill-amber-500 text-amber-500"
              aria-label={t('routes.admin.general.identifiers.table.primary')}
            />
          )}
          <span className="truncate">{row.userEnteredId.length > 0 ? row.userEnteredId : EMPTY_CELL}</span>
        </span>
        {isSaving && (
          <p className="-mb-0.5 mt-0.5 font-sans text-[10px] leading-none text-muted-foreground">
            {t('routes.admin.general.identifiers.row.saving')}
          </p>
        )}
      </DataTableTd>
      <DataTableTd className={`${COMPACT_CELL_CLASS} text-foreground/90 truncate`} dataOffset>
        {row.label.length > 0 ? row.label : EMPTY_CELL}
      </DataTableTd>
      <DataTableTd className={`${COMPACT_CELL_CLASS} font-mono text-xs text-muted-foreground truncate`} dataOffset>
        {row.format !== null && row.format.length > 0 ? row.format : EMPTY_CELL}
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        <input
          type="checkbox"
          checked={row.enforceFormat}
          onChange={() => {
            onToggleEnforceFormat(row);
          }}
          disabled={isDeleting}
          aria-label={t('routes.admin.general.identifiers.row.toggleEnforceFormatAria', {
            label: row.label,
          })}
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
        />
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        <input
          type="checkbox"
          checked={row.allowDuplicates}
          onChange={() => {
            onToggleAllowDuplicates(row);
          }}
          disabled={isDeleting}
          aria-label={t('routes.admin.general.identifiers.row.toggleAllowDuplicatesAria', {
            label: row.label,
          })}
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
        />
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        {row.isPrimary ? (
          // Inlined pill instead of `DataTablePill` because that primitive wraps
          // children in an inner `truncate` span, which mis-renders mixed
          // SVG-plus-text content (the icon ends up stacked above the label).
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-50 text-amber-700 whitespace-nowrap',
              COMPACT_PILL_CLASS,
              'font-medium',
            )}
          >
            <Star className="h-2.5 w-2.5 shrink-0 fill-amber-500 text-amber-500" aria-hidden="true" />
            {t('routes.admin.general.identifiers.row.primary')}
          </span>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              onTogglePrimary(row);
            }}
            disabled={isDeleting}
            aria-label={t('routes.admin.general.identifiers.row.togglePrimaryAria', {
              label: row.label,
            })}
            className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-amber-600"
          >
            <Star className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('routes.admin.general.identifiers.row.makePrimary')}
          </Button>
        )}
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        <input
          type="checkbox"
          checked={row.active}
          onChange={() => {
            onToggleActive(row);
          }}
          disabled={isDeleting}
          aria-label={t('routes.admin.general.identifiers.row.toggleActiveAria', {
            label: row.label,
          })}
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
        />
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} align="right" dataOffset>
        <div className="inline-flex gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              onEdit(row);
            }}
            disabled={isDeleting}
            aria-label={editLabel}
            title={editLabel}
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              onDelete(row);
            }}
            disabled={isDeleting}
            aria-label={deleteLabel}
            title={deleteLabel}
            className={cn(
              'h-6 w-6 text-muted-foreground hover:text-destructive',
              isDeleting && 'opacity-60',
            )}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </DataTableTd>
    </DataTableTr>
  );
}
